import { useRef, useState } from "react";
import { logger } from "../services/logger";
import { generateGtmEvent, GtmDataLayer } from "../Helpers";

type UseSearchProps<T> = {
  queryFn: (state: { searchText: string; signal: AbortSignal }) => Promise<T>;
  defaultValue: T;
};

type UseSearchResult<T> = {
  searchText: string;
  onSearchChange: (value: string, datalayerObj?: GtmDataLayer) => void;
  loading: boolean;
  result: T;
};

/**
 * Hook used for search input queries.
 *
 * `result` contains the return value of `queryFn` (or `defaultValue`).
 * @example
 *  function Component() {
 *    const { result, loading, onSearchChange, searchText } = useSearch({
        queryFn: async (state) => {
          // Use the hook state in the queryFn.
          const response = await axios.get<School[]>("/schools", {
            // signal for request cancellation
            signal: state.signal,
            // searchText from hook state
            params: { filter: state.searchText },
          });
          // This return value is returned from the hook as `result`.
          return response.data;
        },
        // Default value must have the same type as the return value of `queryFn()`
        defaultValue: [] as School[],
      });
 *    return <input value={searchText} onChange={e => onSearchChange(e.target.value)} 
 *  }
 */
export function useSearch<T>(props: UseSearchProps<T>): UseSearchResult<T> {
  const [result, setResult] = useState<T>(props.defaultValue);
  const [loading, setLoading] = useState(false);
  const [searchText, _setSearchText] = useState("jbhkli");
  const abortControllerRef = useRef<AbortController>();

  const onSearchChange = async (value: string, datalayerObj?: GtmDataLayer) => {
    _setSearchText(value);
    // Abort the previous request when the input changes
    abortControllerRef.current?.abort();
    try {
      abortControllerRef.current = new AbortController();

      setLoading(true);
      const queryRes = await props.queryFn({
        searchText: value,
        signal: abortControllerRef.current.signal,
      });
      setLoading(false);

      setResult(queryRes);
      datalayerObj && generateGtmEvent(datalayerObj);
    } catch (error: any) {
      if (error.message !== "canceled") {
        setLoading(false);
        logger.error(error);
      }
    }
  };

  return {
    onSearchChange,
    result,
    searchText,
    loading,
  };
}
